import { useCallback, useRef } from 'react';
import { gql } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';

const QUERY_GET_INTENT = gql`
    query intent($spotId: ID!) {
        intent(spotId: $spotId) {
            id
            date
            time
            length
            quantity
            note
            discountCode
            spotId
            extraIds
        }
    }
`;

const UPDATE_INTENT_MUTATION = gql`
    mutation updateIntent(
        $spotId: ID!
        $date: String
        $time: Float
        $length: Float
        $quantity: Int
        $discountCode: String
        $note: String
        $extraIds: [ID]
        $sessionUuid: String
    ) {
        updateIntent(
            spotId: $spotId
            date: $date
            length: $length
            quantity: $quantity
            discountCode: $discountCode
            note: $note
            time: $time
            extraIds: $extraIds
            sessionUuid: $sessionUuid
        ) {
            date
            time
            length
            quantity
            note
            discountCode
            spotId
            extraIds
        }
    }
`;

/**
 * Runs the saved booking intent query on demand and invokes `onCompletedFunc`
 * with the query data once it settles. Apollo Client v4 removed the
 * `onCompleted` option from `useQuery`/`useLazyQuery`, so completion has to be
 * driven off the promise the execute function returns. The returned promise
 * never rejects, matching the `errorPolicy: 'ignore'` behaviour of this query.
 *
 * @param {Function} onCompletedFunc - invoked with the resolved query data
 */
export function useLazyGetIntentCal(onCompletedFunc) {
    const [execute, { data, loading, called }] = useLazyQuery(QUERY_GET_INTENT, {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'ignore',
    });
    const onCompletedRef = useRef(onCompletedFunc);
    onCompletedRef.current = onCompletedFunc;

    const getIntent = useCallback(
        (options) =>
            execute(options).then(
                (result) => {
                    onCompletedRef.current?.(result?.data);
                    return result;
                },
                (error) => {
                    onCompletedRef.current?.(undefined);
                    return { data: undefined, error };
                }
            ),
        [execute]
    );

    return { getIntent, intent: data?.intent, loading, called };
}

export function useUpdateIntent() {
    const [updateIntent, { loading, data }] = useMutation(UPDATE_INTENT_MUTATION);
    return { updateIntent, loading, data: data?.updateIntent };
}
